import { useEffect, useState } from "react";
import { ScrollProgress } from "./ScrollProgress";
import { CompletionRing } from "./CompletionRing";
import { useReveal } from "@/hooks/use-reveal";

interface Props {
  /** Persistent key so the completion ring only fires once per user per reading. */
  completionKey: string;
}

/**
 * Reading chrome: top-of-page golden progress bar, one-time completion ring
 * when the user reaches the end of the article, and reveal-on-scroll wiring.
 */
export function ReadingChrome({ completionKey }: Props) {
  useReveal();
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storageKey = `ka.completed.${completionKey}`;
    if (window.localStorage.getItem(storageKey)) return;

    let raf = 0;
    const check = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      if (max <= 0) return;
      const ratio = doc.scrollTop / max;
      if (ratio >= 0.97) {
        try { window.localStorage.setItem(storageKey, String(Date.now())); } catch { /* private mode */ }
        setComplete(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; check(); });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [completionKey]);

  return (
    <>
      <ScrollProgress />
      <CompletionRing active={complete} />
    </>
  );
}
